import { Controller } from "@hotwired/stimulus"
import { savePlan, downscaleImageDataUrl, compressCanvasToTarget, dataUrlBytes } from "plan_storage"
import { ageGroupLabel } from "client_age"

// Client section controller — owns the dynamic bits the plan-store
// controller can't handle generically:
//
//   - DDS Age Group readout derived from client.dob
//   - Photo upload (FileReader → base64) for client.photo and client.lifePhoto
//   - Initial preview hydration from the saved plan
//
// Mirrors the renderEditContent('client') branch of pcp_builder.html
// so the section behaves identically to the iframe editor.

const STORAGE_KEY = "pcp_plan_data"

// A photo (data URL) larger than this can push the shared plan over the
// localStorage quota, which then blocks ALL saves. Fallback paths that can't
// crop/compress route through _commitPhotoGuarded to stay under it.
const MAX_PHOTO_BYTES = 1_600_000

export default class extends Controller {
  static targets = ["ageGroup", "photoPreview", "photoEmpty", "lifePhotoPreview", "lifePhotoEmpty", "photoRemoveBtn", "lifePhotoRemoveBtn"]

  connect() {
    this.plan = this._load()
    this.refreshAgeGroup()
    this.refreshPhoto("photo")
    this.refreshPhoto("lifePhoto")
    // Heal plans that were saved before photo-downscaling existed: a raw
    // multi-MB photo already in localStorage keeps the plan over quota, so
    // every new edit fails to save. Shrink it in place on load so saving
    // works again (fire-and-forget; visual result is identical).
    this._shrinkExistingPhotos()
    // Re-render when plan-store fires plan:change so DOB edits update
    // the age-group pill without a full re-render.
    this._onPlanChange = (e) => {
      const path = e.detail && e.detail.path
      if (path === "client.dob") {
        this.plan = e.detail.plan
        this.refreshAgeGroup()
      }
    }
    document.addEventListener("plan:change", this._onPlanChange)
  }

  disconnect() {
    document.removeEventListener("plan:change", this._onPlanChange)
  }

  refreshAgeGroup() {
    if (!this.hasAgeGroupTarget) return
    const dob = (this.plan && this.plan.client && this.plan.client.dob) || ""
    // "—" when the DOB is empty or invalid (the old code mislabeled both as
    // "Adult"); a real group label otherwise.
    this.ageGroupTarget.textContent = ageGroupLabel(dob)
  }

  refreshPhoto(field) {
    const previewName = field === "lifePhoto" ? "lifePhotoPreviewTarget" : "photoPreviewTarget"
    const emptyName   = field === "lifePhoto" ? "lifePhotoEmptyTarget"   : "photoEmptyTarget"
    const removeName  = field === "lifePhoto" ? "lifePhotoRemoveBtnTarget" : "photoRemoveBtnTarget"
    const has = (n) => this[`has${n.charAt(0).toUpperCase() + n.slice(1)}`]
    if (!has(previewName) || !has(emptyName)) return
    const data = (this.plan && this.plan.client && this.plan.client[field]) || ""
    const img = this[previewName]
    const empty = this[emptyName]
    if (data) {
      img.src = data
      img.classList.remove("hidden")
      empty.classList.add("hidden")
    } else {
      img.removeAttribute("src")
      img.classList.add("hidden")
      empty.classList.remove("hidden")
    }
    // The Remove button shows only while there's a photo to remove.
    if (has(removeName)) this[removeName].classList.toggle("hidden", !data)
  }

  // Remove an attached photo (client.photo / client.lifePhoto).
  removePhoto(event) {
    event?.preventDefault?.()
    const field = event.currentTarget.dataset.photoField
    if (!field) return
    if (!window.confirm("Remove this photo?")) return
    if (!this.plan) this.plan = this._load()
    if (!this.plan.client) this.plan.client = {}
    this.plan.client[field] = ""
    this._save()
    this.refreshPhoto(field)
    document.dispatchEvent(new CustomEvent("plan:change", {
      bubbles: true,
      detail: { path: `client.${field}`, value: "", plan: this.plan }
    }))
  }

  uploadPhoto(event) {
    const input = event.currentTarget
    const file = input.files && input.files[0]
    const field = input.dataset.photoField
    input.value = ""  // allow re-picking the same file later
    if (!file || !field) return
    const reader = new FileReader()
    reader.onload = (e) => this._openCropModal(e.target.result, field)
    reader.readAsDataURL(file)
  }

  // Store a finished (cropped + compressed) photo data URL and notify the
  // other panes. Every path that produces a photo funnels through here.
  _commitPhoto(dataUrl, field) {
    if (!this.plan) this.plan = this._load()
    if (!this.plan.client) this.plan.client = {}
    this.plan.client[field] = dataUrl
    this._save()
    this.refreshPhoto(field)
    document.dispatchEvent(new CustomEvent("plan:change", {
      bubbles: true,
      detail: { path: `client.${field}`, value: dataUrl, plan: this.plan }
    }))
  }

  // Fallback commit for paths that couldn't crop/compress (undecodable image,
  // canvas failure). Shrink an oversized data URL before storing it, and refuse
  // with a clear message if it still won't fit — so a giant photo can never
  // silently push the plan over quota and block every future save.
  async _commitPhotoGuarded(dataUrl, field) {
    let out = dataUrl
    if (dataUrlBytes(out) > 1_200_000) {
      try { out = await downscaleImageDataUrl(out, 1000, 0.7) } catch (_) { out = dataUrl }
    }
    if (dataUrlBytes(out) > MAX_PHOTO_BYTES) {
      window.alert("That image is too large to store even after shrinking. Please choose a smaller photo (a JPEG or PNG usually works best).")
      return
    }
    this._commitPhoto(out, field)
  }

  // Crop-and-compress modal shown after a photo is chosen. The user can
  // drag/resize a crop box (or keep the whole photo); on save we crop to
  // that region and compress to under 1 MB so it never blows the shared
  // localStorage budget. Falls back to a plain downscale if the image
  // can't be decoded (e.g. HEIC on desktop).
  _openCropModal(dataUrl, field) {
    const img = new Image()
    // Undecodable (e.g. HEIC on desktop): shrink-and-store via the guard so an
    // oversized original can't lock saving out.
    img.onerror = () => this._commitPhotoGuarded(dataUrl, field)
    img.onload = () => this._buildCropUI(img, dataUrl, field)
    img.src = dataUrl
  }

  _buildCropUI(img, dataUrl, field) {
    const nW = img.naturalWidth, nH = img.naturalHeight
    const maxW = Math.min(560, window.innerWidth - 64)
    const maxH = Math.min(440, window.innerHeight - 240)
    const fit = Math.min(maxW / nW, maxH / nH, 1)
    const dW = Math.round(nW * fit), dH = Math.round(nH * fit)

    const overlay = document.createElement("div")
    overlay.setAttribute("role", "dialog")
    overlay.setAttribute("aria-modal", "true")
    overlay.style.cssText = "position:fixed;inset:0;z-index:10000;display:flex;align-items:center;justify-content:center;background:rgba(15,23,42,.6);backdrop-filter:blur(2px);font:14px/1.4 'Inter',ui-sans-serif,system-ui,sans-serif;padding:16px;"
    overlay.innerHTML = `
      <div style="background:#fff;border-radius:14px;box-shadow:0 24px 48px rgba(0,0,0,.35);max-width:640px;width:100%;overflow:hidden;">
        <div style="padding:16px 20px 4px;">
          <h3 style="margin:0;font-size:16px;font-weight:700;color:#0f172a;">Crop photo</h3>
          <p style="margin:4px 0 0;font-size:12.5px;color:#64748b;">Drag the box to crop, or keep the whole photo. It's auto-compressed to under 1&nbsp;MB.</p>
        </div>
        <div style="padding:14px 20px;display:flex;justify-content:center;background:#0f172a;">
          <div data-role="stage" style="position:relative;width:${dW}px;height:${dH}px;touch-action:none;user-select:none;">
            <img src="${dataUrl}" draggable="false" style="width:${dW}px;height:${dH}px;display:block;border-radius:4px;">
            <div data-role="crop" style="position:absolute;box-sizing:border-box;border:1.5px solid #fff;box-shadow:0 0 0 9999px rgba(0,0,0,.5);cursor:move;">
              ${["nw", "ne", "sw", "se"].map(h => `<span data-h="${h}" style="position:absolute;width:14px;height:14px;background:#4f46e5;border:2px solid #fff;border-radius:3px;${h[0] === "n" ? "top:-8px;" : "bottom:-8px;"}${h[1] === "w" ? "left:-8px;" : "right:-8px;"}cursor:${h === "nw" || h === "se" ? "nwse" : "nesw"}-resize;"></span>`).join("")}
            </div>
          </div>
        </div>
        <div style="padding:14px 20px 18px;display:flex;align-items:center;gap:8px;">
          <span data-role="hint" style="font-size:12px;color:#64748b;flex:1;">Original ${nW}×${nH}</span>
          <button type="button" data-role="cancel" style="padding:8px 14px;font:600 13px/1 inherit;background:#f1f5f9;color:#334155;border:1px solid #e2e8f0;border-radius:8px;cursor:pointer;">Cancel</button>
          <button type="button" data-role="full" style="padding:8px 14px;font:600 13px/1 inherit;background:#eef2ff;color:#4338ca;border:1px solid #c7d2fe;border-radius:8px;cursor:pointer;">Use full photo</button>
          <button type="button" data-role="apply" style="padding:8px 14px;font:700 13px/1 inherit;background:#4f46e5;color:#fff;border:1px solid #4f46e5;border-radius:8px;cursor:pointer;">Crop &amp; save</button>
        </div>
      </div>`
    document.body.appendChild(overlay)

    const stage = overlay.querySelector('[data-role="stage"]')
    const box   = overlay.querySelector('[data-role="crop"]')
    const MIN = 40
    // Crop box in display coords; start at 80% centered.
    let cw = Math.round(dW * 0.8), ch = Math.round(dH * 0.8)
    let cx = Math.round((dW - cw) / 2), cy = Math.round((dH - ch) / 2)
    const paint = () => {
      box.style.left = cx + "px"; box.style.top = cy + "px"
      box.style.width = cw + "px"; box.style.height = ch + "px"
    }
    paint()

    let drag = null
    const onDown = (e) => {
      const handle = e.target.dataset && e.target.dataset.h
      drag = { handle, sx: e.clientX, sy: e.clientY, cx, cy, cw, ch }
      e.preventDefault(); e.stopPropagation()
      document.addEventListener("pointermove", onMove)
      document.addEventListener("pointerup", onUp)
    }
    const onMove = (e) => {
      if (!drag) return
      const dx = e.clientX - drag.sx, dy = e.clientY - drag.sy
      if (!drag.handle) {
        cx = Math.min(Math.max(0, drag.cx + dx), dW - cw)
        cy = Math.min(Math.max(0, drag.cy + dy), dH - ch)
      } else {
        let l = drag.cx, t = drag.cy, r = drag.cx + drag.cw, b = drag.cy + drag.ch
        if (drag.handle[1] === "w") l = Math.min(Math.max(0, drag.cx + dx), r - MIN)
        else                       r = Math.max(Math.min(dW, r + dx), l + MIN)
        if (drag.handle[0] === "n") t = Math.min(Math.max(0, drag.cy + dy), b - MIN)
        else                       b = Math.max(Math.min(dH, b + dy), t + MIN)
        cx = l; cy = t; cw = r - l; ch = b - t
      }
      paint()
    }
    const onUp = () => {
      drag = null
      document.removeEventListener("pointermove", onMove)
      document.removeEventListener("pointerup", onUp)
    }
    box.addEventListener("pointerdown", onDown)

    const onKey = (e) => { if (e.key === "Escape") close() }
    const close = () => {
      overlay.remove()
      document.removeEventListener("keydown", onKey)
    }
    const scale = nW / dW
    const finish = (useFull) => {
      // Tear the modal down FIRST so a canvas / encode failure can never
      // leave the full-screen overlay stuck over the editor (which reads
      // as "the app froze after saving the photo").
      close()
      try {
        const sx = useFull ? 0 : Math.round(cx * scale)
        const sy = useFull ? 0 : Math.round(cy * scale)
        const sw = useFull ? nW : Math.round(cw * scale)
        const sh = useFull ? nH : Math.round(ch * scale)
        const canvas = document.createElement("canvas")
        canvas.width = sw; canvas.height = sh
        canvas.getContext("2d").drawImage(img, sx, sy, sw, sh, 0, 0, sw, sh)
        let out = null
        try { out = compressCanvasToTarget(canvas, 1_000_000, 1400) } catch (_) {}
        if (out) this._commitPhoto(out, field)
        else this._commitPhotoGuarded(dataUrl, field)
      } catch (_) {
        // Canvas blew up (huge dimensions / decode) — shrink-and-store the
        // original via the guard so the user still gets a photo without risking
        // a quota lockout.
        this._commitPhotoGuarded(dataUrl, field)
      }
    }
    overlay.querySelector('[data-role="cancel"]').addEventListener("click", close)
    overlay.querySelector('[data-role="full"]').addEventListener("click", () => finish(true))
    overlay.querySelector('[data-role="apply"]').addEventListener("click", () => finish(false))
    overlay.addEventListener("click", (e) => { if (e.target === overlay) close() })
    document.addEventListener("keydown", onKey)
  }

  // One-time repair for plans stored before downscaling existed: replace
  // any oversized photo blob with a downscaled version and re-save, so the
  // plan drops back under quota and saving resumes.
  async _shrinkExistingPhotos() {
    const client = this.plan && this.plan.client
    if (!client) return
    let changed = false
    for (const field of ["photo", "lifePhoto"]) {
      const v = client[field]
      // Only heal genuinely oversized legacy photos (> ~1.1 MB). New photos
      // are already cropped/compressed under 1 MB and are left untouched.
      if (typeof v === "string" && v.startsWith("data:image") && dataUrlBytes(v) > 1_100_000) {
        try { client[field] = await downscaleImageDataUrl(v, 1000, 0.72); changed = true }
        catch (_) { /* leave as-is on decode failure */ }
      }
    }
    if (changed) {
      this._save()
      this.refreshPhoto("photo")
      this.refreshPhoto("lifePhoto")
      // Nudge the other panes to reload the now-smaller plan so they don't
      // re-save the oversized copy from their stale in-memory object.
      document.dispatchEvent(new CustomEvent("plan:change", {
        bubbles: true, detail: { path: "client.photo", plan: this.plan, source: "client-section" }
      }))
    }
  }

  _load() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY)
      return raw ? JSON.parse(raw) : {}
    } catch (_) { return {} }
  }

  _save() {
    return savePlan(this.plan)
  }
}
